import dayjs from 'dayjs'

import { LocaleProfileKeysType } from 'shared/locale/en-US'

export const defaultDateFormatView = 'DD/MM/YYYY'
export const defaultDateFullFormatView = 'DD/MM/YYYY HH:mm'
export const defaultDateFormatConvert = 'YYYY-MM-DDTHH:mmZ'

export enum PeriodName {
  TODAY = 'TODAY',
  YESTERDAY = 'YESTERDAY',
  LAST_THREE_DAYS = 'LAST_THREE_DAYS',
  LAST_WEEK = 'LAST_WEEK',
  CUSTOM = 'CUSTOM',
}

interface IPeriodItem {
  name: PeriodName
  locale: LocaleProfileKeysType
  beginDate: string
  endDate: string
}

export const periodList: IPeriodItem[] = [
  {
    name: PeriodName.TODAY,
    locale: 'today',
    beginDate: dayjs().startOf('day').format(defaultDateFormatConvert),
    endDate: dayjs().endOf('day').format(defaultDateFormatConvert),
  },
  {
    name: PeriodName.YESTERDAY,
    locale: 'yesterday',
    beginDate: dayjs().startOf('day').subtract(1, 'day').format(defaultDateFormatConvert),
    endDate: dayjs().endOf('day').subtract(1, 'day').format(defaultDateFormatConvert),
  },
  {
    name: PeriodName.LAST_THREE_DAYS,
    locale: 'lastThreeDays',
    beginDate: dayjs().startOf('day').subtract(3, 'day').format(defaultDateFormatConvert),
    endDate: dayjs().endOf('day').format(defaultDateFormatConvert),
  },
  {
    name: PeriodName.LAST_WEEK,
    locale: 'lastWeek',
    beginDate: dayjs().startOf('week').subtract(1, 'week').format(defaultDateFormatConvert),
    endDate: dayjs().endOf('week').subtract(1, 'week').format(defaultDateFormatConvert),
  },
  {
    name: PeriodName.CUSTOM,
    locale: 'custom',
    beginDate: '',
    endDate: '',
  },
]

export const getPeriod = (period: PeriodName): IPeriodItem => periodList.filter(p => p.name === period)[0]

export const startOfDay = (date: Date | string) => dayjs(date).startOf('day').toDate()

export const startOfMonth = (date: Date | string) => dayjs(date).startOf('month').toDate()

export const dateToServerFormat = (date: Date | string) => dayjs(date).format('YYYY-MM-DD')

export const endOfDay = (date: Date | string) => dayjs(date).endOf('day').toDate()

export const endOfMonth = (date: Date | string) => dayjs(date).endOf('month').toDate()

export const formatDDMMYYYY = (date: Date | string) => dayjs(date).format('DD/MM/YYYY')
export const formatDDMMYYHHMM = (date: Date | string) => dayjs(date).format('DD/MM/YY HH:mm')

export const addMonths = (date: Date | string, amount: number) => dayjs(date).add(amount, 'month').toDate()

export const subMonths = (date: Date | string, amount: number) => dayjs(date).subtract(amount, 'month').toDate()

export const subDays = (date: Date | string, amount: number) => dayjs(date).subtract(amount, 'day').toDate()

export const addYears = (date: Date | string, amount: number) => dayjs(date).add(amount, 'year').toDate()

export const subYears = (date: Date | string, amount: number) => dayjs(date).subtract(amount, 'year').toDate()

export const eachDayOfInterval = (interval: { start: Date | string; end: Date | string }) => {
  const { start, end } = interval
  const startDate = dayjs(start).startOf('day')
  const endDate = dayjs(end).startOf('day')

  if (endDate.isBefore(startDate)) {
    throw new Error('End date must be after start date')
  }

  const days: Date[] = []
  let currentDate = startDate

  while (currentDate.isBefore(endDate) || currentDate.isSame(endDate, 'day')) {
    days.push(currentDate.toDate())
    currentDate = currentDate.add(1, 'day')
  }

  return days
}

export const eachMonthOfInterval = (interval: { start: Date | string; end: Date | string }) => {
  const { start, end } = interval
  const startDate = dayjs(start).startOf('month')
  const endDate = dayjs(end).startOf('month')

  if (endDate.isBefore(startDate)) {
    throw new Error('End date must be after start date')
  }

  const months: Date[] = []
  let currentDate = startDate

  while (currentDate.isBefore(endDate) || currentDate.isSame(endDate, 'month')) {
    months.push(currentDate.toDate())
    currentDate = currentDate.add(1, 'month')
  }

  return months
}

export const addDays = (date: Date | string, amount: number) => dayjs(date).add(amount, 'day').toDate()

export const startOfYear = (date: Date | string) => dayjs(date).startOf('year').toDate()

export const endOfYear = (date: Date | string) => dayjs(date).endOf('year').toDate()
